import { Authenticated, CanAccess, GitHubBanner, Refine } from "@refinedev/core";
// import { RefineKbar, RefineKbarProvider } from "@refinedev/kbar";

import {
  ErrorComponent,
  ThemedLayout as ThemedLayoutV2,
  ThemedSider as ThemedSiderV2,
  useNotificationProvider,
} from "@refinedev/antd";
import { ExportOutlined, HeartOutlined } from "@ant-design/icons";
import "@refinedev/antd/dist/reset.css";

import routerBindings, {
  CatchAllNavigate,
  DocumentTitleHandler,
  NavigateToResource,
  UnsavedChangesNotifier,
} from "@refinedev/react-router";
import { NuqsAdapter } from "nuqs/adapters/react-router/v7";
import { dataProvider } from "./dataProvider";
import { App as AntdApp } from "antd";
import { BrowserRouter, Outlet, Route, Routes } from "react-router";
import { authProvider } from "./authProvider";
import { accessControlProvider } from "./accessControlProvider";
import { Header } from "./components/header";
import { ColorModeContextProvider } from "./contexts/color-mode";
import { ConcentratorList } from "./pages/concentrators";
import { Meters } from "./pages/meters";
import { MeterChart } from "./pages/meters/chart";
import { Export } from "./pages/export";
import { FlexibleExport } from "./pages/export/flexible";
import { UserSettings } from "./pages/me";
import { HealthPage } from "./pages/health";
import { HealthDetailPage } from "./pages/health/detail";
import { ForgotPassword } from "./pages/forgotPassword";
import { ResetPasswordKey } from "./pages/resetPasswordKey";
import { Login } from "./pages/login";
import { Register } from "./pages/register";
import { InvitationStatus } from "./pages/invitationStatus";
import LogoutPage from "./pages/logout";
import { RemoteDenied } from "./pages/RemoteDenied";
import { RemoteErrors } from "./pages/RemoteErrors";

function App() {

  return (
    <BrowserRouter>
      <NuqsAdapter>
        {/* <RefineKbarProvider> */}
        <ColorModeContextProvider>
          <AntdApp>
            <Refine
              dataProvider={dataProvider("/api")}
              notificationProvider={useNotificationProvider}
              routerProvider={routerBindings}
              authProvider={authProvider}
              accessControlProvider={accessControlProvider}
              resources={[
                {
                  name: "concentrators",
                  list: "/concentrators",
                  // create: "/concentrators/create",
                  // edit: "/concentrators/edit/:id",
                  // show: "/concentrators/show/:id",
                  meta: {
                    canDelete: true,
                  },
                },
                {
                  name: "export",
                  list: "/export",
                  meta: {
                    icon: <ExportOutlined />,
                    label: "Export"
                  },
                },
                {
                  name: "health",
                  list: "/health",
                  meta: {
                    icon: <HeartOutlined />,
                    label: "System Health",
                  },
                },
              ]}
              options={{
                syncWithLocation: true,
                warnWhenUnsavedChanges: true,
                projectId: "q9dXRF-7ADltz-XcDqCb",
                disableTelemetry: true
              }}
            >
              <Routes>
                {/* Logout route - must be before authenticated routes to avoid catch-all */}
                <Route path="/logout" element={<LogoutPage />} />
                <Route
                  element={
                    <Authenticated
                      key="authenticated-inner"
                      fallback={<CatchAllNavigate to="/login" />}
                    >
                      <ThemedLayoutV2
                        Header={Header}
                        Sider={(props) => (
                          <ThemedSiderV2
                            {...props}
                            fixed
                            render={({ items }) => items}
                            Title={({ collapsed }) => (
                              <div style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: collapsed ? "center" : "flex-start",
                                gap: "8px",
                                padding: "16px",
                                fontWeight: "bold",
                                fontSize: collapsed ? "14px" : "16px"
                              }}>
                                {collapsed ? "SMC" : "Smart Metering Cloud"}
                              </div>
                            )}
                          />
                        )}
                      >
                        <Outlet />
                      </ThemedLayoutV2>
                    </Authenticated>
                  }
                >
                  <Route
                    index
                    element={<NavigateToResource resource="concentrators" />}
                  />
                  <Route path="/concentrators">
                    <Route index element={<ConcentratorList />} />
                    {/* <Route path="create" element={<ConcentratorCreate />} />
                    <Route path="edit/:id" element={<ConcentratorEdit />} />
                    <Route path="show/:id" element={<ConcentratorShow />} /> */}
                  </Route>
                  <Route path="/concentrators/:concentratorID/meters/:media" element={<Meters />} />
                  <Route path="/concentrators/:concentratorID/meters/:media/:viewMode" element={<Meters />} />
                  <Route path="/meters/:meterPk/chart" element={<MeterChart />} />
                  <Route path="/concentrators/:concentratorID/meters/:meterID/chart" element={<MeterChart />} />
                  <Route path="/export" element={<Export />} />
                  <Route path="/export/flexible" element={<FlexibleExport />} />
                  <Route
                    path="/health"
                    element={
                      <CanAccess resource="health" action="list" fallback={<ErrorComponent />}>
                        <HealthPage />
                      </CanAccess>
                    }
                  />
                  <Route
                    path="/health/:concentratorId"
                    element={
                      <CanAccess resource="health" action="list" fallback={<ErrorComponent />}>
                        <HealthDetailPage />
                      </CanAccess>
                    }
                  />
                  <Route path="/me" element={<UserSettings />} />
                  <Route path="*" element={<ErrorComponent />} />
                </Route>
                <Route
                  element={
                    <Authenticated
                      key="authenticated-outer"
                      fallback={<Outlet />}
                    >
                      <NavigateToResource />
                    </Authenticated>
                  }
                >
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route
                    path="/forgot-password"
                    element={<ForgotPassword />}
                  />
                  <Route
                    path="/account/password/reset"
                    element={<ForgotPassword />}
                  />
                <Route
                  path="/account/password/reset/key/:key"
                  element={<ResetPasswordKey />}
                />
                <Route path="/invitation-status" element={<InvitationStatus />} />
                </Route>
                <Route path="/remote-error/:code" element={<RemoteErrors />} />
                <Route path="/remote-denied" element={<RemoteDenied />} />
              </Routes>

              {/* <RefineKbar /> */}
              <UnsavedChangesNotifier />
              <DocumentTitleHandler 
                handler={({ resource, action, params }) => {
                  const baseTitle = "Smart Tech App";
                  if (resource?.name === "concentrators") {
                    if (action === "list") return `${baseTitle} - Concentrators`;
                    if (params?.concentratorID) return `${baseTitle} - Concentrator ${params.concentratorID}`;
                  }
                  return baseTitle;
                }}
              />
            </Refine>
          </AntdApp>
        </ColorModeContextProvider>
        {/* </RefineKbarProvider> */}
      </NuqsAdapter>
    </BrowserRouter>
  );
}

export default App;
